
import { useParams } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { useGroupPage } from "@/hooks/useGroupPage";
import { currencies } from "@/constants/currencies";
import { GroupContent } from "@/components/groups/GroupContent";
import { GroupDataFetcher } from "@/components/groups/GroupDataFetcher";
import { GroupRealtimeSubscriptions } from "@/components/groups/GroupRealtimeSubscriptions";
import { ReceiptScanner } from "@/components/receipts/ReceiptScanner"; // Import the ReceiptScanner
import { useGroupReceipts } from "@/hooks/useGroupReceipts"; // Import the receipt hook

const GroupPage = () => {
  const { groupId } = useParams<{ groupId: string }>();
  
  // Get all functionality from the hook
  const {
    expenses,
    members,
    groupName,
    groupStatus,
    showExpenseForm,
    showAddMember,
    showShareLink,
    session,
    editingExpenseId,
    settlements,
    loading,
    expenseFormState,
    
    setGroupName,
    setGroupStatus,
    setShowExpenseForm,
    setShowAddMember,
    setShowShareLink,
    setSession,
    
    fetchMembers,
    fetchExpenses,
    fetchSettlements,
    handleRemoveMember,
    handleExpenseSubmit,
    handleEditExpense,
    handleSettlementUpdate,
    handleDeleteExpense,
  } = useGroupPage(groupId);
  
  // Initialize the receipt scanner for this group
  const receiptHook = useGroupReceipts(groupId || "");
  
  // Handle scan receipt action (different from add expense)
  const handleScanReceipt = () => {
    receiptHook.handleScanReceipt();
  };
  
  // Pre-populate the expense form with receipt data when it's available
  if (receiptHook.receiptData && !showExpenseForm) {
    // Open the expense form with receipt data
    expenseFormState.title = receiptHook.receiptData.shop_name || "Receipt Expense";
    expenseFormState.amount = receiptHook.receiptData.total_amount || "0";
    expenseFormState.currency = "JPY"; // Default to JPY for receipts
    // Reset receipt data after using it
    receiptHook.setReceiptData(null);
    // Show the expense form
    setShowExpenseForm(true);
  }
  
  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <Header />
      
      {/* Data fetching component */}
      <GroupDataFetcher 
        groupId={groupId}
        setSession={setSession}
        setGroupName={setGroupName}
        setGroupStatus={setGroupStatus}
        fetchMembers={fetchMembers}
        fetchExpenses={fetchExpenses}
        fetchSettlements={fetchSettlements}
      />
      
      {/* Real-time subscriptions component */}
      {groupId && (
        <GroupRealtimeSubscriptions
          groupId={groupId}
          fetchMembers={fetchMembers}
          fetchExpenses={fetchExpenses}
          fetchSettlements={fetchSettlements}
        />
      )}
      
      {/* Receipt Scanner Component */}
      <ReceiptScanner
        open={receiptHook.isOpen}
        onClose={receiptHook.closeScanner}
        onCapture={receiptHook.processReceipt}
        isProcessing={receiptHook.isProcessing}
      />
      
      {/* Main content */}
      <GroupContent
        groupId={groupId || ''}
        groupName={groupName}
        groupStatus={groupStatus}
        expenses={expenses}
        members={members}
        settlements={settlements}
        currencies={currencies}
        showExpenseForm={showExpenseForm}
        showAddMember={showAddMember}
        showShareLink={showShareLink}
        session={session}
        editingExpenseId={editingExpenseId}
        expenseFormState={expenseFormState}
        setShowExpenseForm={setShowExpenseForm}
        setShowAddMember={setShowAddMember}
        setShowShareLink={setShowShareLink}
        handleRemoveMember={handleRemoveMember}
        handleExpenseSubmit={handleExpenseSubmit}
        handleEditExpense={handleEditExpense}
        handleDeleteExpense={handleDeleteExpense}
        handleSettlementUpdate={handleSettlementUpdate}
        fetchExpenses={fetchExpenses}
        fetchSettlements={fetchSettlements}
        handleScanReceipt={handleScanReceipt} // Add scan receipt handler
      />
    </div>
  );
};

export default GroupPage;
